import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Folder,
  Menu,
  X,
  FileText,
  ArrowRightLeft,
  LayoutDashboard,
  User,
  Users,
  LogOut,
  Maximize2,
  Minimize2,
  Building2,
  Search,
  LayoutGrid,
  MessageCircle,
  Clock,
  Shield,
  HardDrive,
  SlidersHorizontal,
  ChevronDown,
  ChevronRight,
  CircleHelp,
} from 'lucide-react';
import { useAuthStore } from 'stores/authStore';
import { useCommandPaletteStore } from 'stores/commandPaletteStore';
import { useUserPreferencesStore } from 'stores/userPreferencesStore';
import styles from './Sidebar.module.scss';
import { ROUTES } from 'utils/constants';

const Sidebar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const authLogout = useAuthStore((state) => state.logout);
  const location = useLocation();
  const { pathname } = location;
  const navigate = useNavigate();
  const betaEnabled = useUserPreferencesStore((s) => s.betaEnabled);

  type SidebarGroupKey =
    'rbac' | 'church' | 'churchReports' | 'accounting' | 'others' | 'account';

  const isInProducts = (path: string) => path.startsWith(ROUTES.PRODUCTS.ROOT);
  const isInRbac = (path: string) =>
    path.startsWith(ROUTES.PERMISSION.ROOT) ||
    path.startsWith(ROUTES.ROLE.ROOT);
  const isInChurch = (path: string) =>
    path.startsWith(ROUTES.MEMBERSHIP.ROOT) ||
    path.startsWith(ROUTES.CHURCH.REPORTS.ROOT);
  const isInSalesExpenses = (path: string) =>
    path.startsWith(ROUTES.SALES.ROOT) ||
    path.startsWith(ROUTES.EXPENSES.ROOT) ||
    path.startsWith(ROUTES.V2.SALES_ORDERS) ||
    path.startsWith(ROUTES.V2.SALES) ||
    path.startsWith(ROUTES.V2.PURCHASE_ORDERS) ||
    path.startsWith(ROUTES.V2.PURCHASES);
  const isInReports = (path: string) => path.startsWith(ROUTES.REPORTS.ROOT);
  const isInOthers = (path: string) =>
    path.startsWith(ROUTES.STORAGE.ROOT) ||
    path.startsWith(ROUTES.SCHEDULES.ROOT) ||
    path.startsWith(ROUTES.KANBAN) ||
    path.startsWith(ROUTES.FORMS) ||
    path.startsWith(ROUTES.CHAT);
  const isInAccount = (path: string) =>
    path.startsWith(ROUTES.USER.PROFILE) ||
    path.startsWith(ROUTES.USER.SETTINGS) ||
    path.startsWith(ROUTES.USER.CHANGE_PASSWORD) ||
    path.startsWith(ROUTES.USER.COMPLETE_PROFILE) ||
    path.startsWith(ROUTES.COMPANIES.ROOT);
  const isInHelp = (path: string) => path.startsWith(ROUTES.HELP);

  const [expandedGroups, setExpandedGroups] = useState<
    Record<SidebarGroupKey, boolean>
  >(() => ({
    rbac: isInRbac(pathname),
    church: isInChurch(pathname),
    churchReports: pathname.startsWith(ROUTES.CHURCH.REPORTS.ROOT),
    accounting:
      isInSalesExpenses(pathname) ||
      isInReports(pathname) ||
      isInProducts(pathname),
    others: isInOthers(pathname),
    account: isInAccount(pathname),
  }));

  useEffect(() => {
    // Keep active groups expanded while navigating; user can still manually collapse.
    setExpandedGroups((prev) => {
      const next = { ...prev };
      if (isInRbac(pathname)) next.rbac = true;
      if (isInChurch(pathname)) next.church = true;
      if (pathname.startsWith(ROUTES.CHURCH.REPORTS.ROOT)) {
        next.churchReports = true;
      }
      if (
        isInSalesExpenses(pathname) ||
        isInReports(pathname) ||
        isInProducts(pathname)
      )
        next.accounting = true;
      if (isInOthers(pathname)) next.others = true;
      if (isInAccount(pathname)) next.account = true;
      return next;
    });
  }, [pathname]);

  const toggleGroup = (key: SidebarGroupKey) => {
    setExpandedGroups((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const toggleSidebar = () => {
    setIsOpen(!isOpen);
  };

  const closeSidebar = () => {
    setIsOpen(false);
  };

  const handleViewProfile = () => {
    closeSidebar();
    navigate(ROUTES.USER.PROFILE);
  };

  const handleLogout = async () => {
    closeSidebar();
    await authLogout();
    navigate(ROUTES.HOME, { replace: true });
  };

  useEffect(() => {
    if (typeof document === 'undefined') return;

    const handleFullscreenChange = () => {
      setIsFullscreen(Boolean(document.fullscreenElement));
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);

  const toggleFullscreen = async () => {
    if (typeof document === 'undefined') return;

    try {
      if (!document.fullscreenElement) {
        await document.documentElement.requestFullscreen();
      } else if (document.exitFullscreen) {
        await document.exitFullscreen();
      }
    } catch (error) {
      console.error('Failed to toggle fullscreen', error);
    } finally {
      closeSidebar();
    }
  };

  const openCommandPalette = useCommandPaletteStore((s) => s.open);

  const handleSearch = () => {
    closeSidebar();
    openCommandPalette();
  };

  const handleNavigateToDashboard = () => {
    closeSidebar();
    navigate(ROUTES.DASHBOARD);
  };

  return (
    <>
      {/* Burger Icon Button */}
      {!isOpen && (
        <button
          className={styles.burgerButton}
          onClick={toggleSidebar}
          aria-label="Toggle sidebar"
        >
          <Menu size={16} />
        </button>
      )}

      {/* Overlay */}
      {isOpen && <div className={styles.overlay} onClick={closeSidebar} />}

      {/* Sidebar */}
      <aside className={`${styles.sidebar} ${isOpen ? styles.open : ''}`}>
        <div className={styles.sidebarHeader}>
          <button
            type="button"
            onClick={handleNavigateToDashboard}
            className={styles.sidebarHeaderContent}
          >
            <img
              src="/android-chrome-192x192.png"
              alt="logo"
              className={styles.sidebarLogo}
            />
            <span className={styles.sidebarTitle}>Abrasoft Hub</span>
          </button>
          <button
            className={styles.closeButton}
            onClick={closeSidebar}
            aria-label="Close sidebar"
          >
            <X size={16} strokeWidth={2.5} />
          </button>
        </div>
        <nav className={styles.nav}>
          <ul className={styles.navList}>
            {/* Dashboard */}
            <li className={styles.navListItem}>
              <Link
                to={ROUTES.DASHBOARD}
                className={styles.navLink}
                onClick={closeSidebar}
              >
                <span>
                  <LayoutDashboard size={19} strokeWidth={1.8} />
                </span>
                Dashboard
              </Link>
            </li>

            {/* Search */}
            <li className={styles.navListItem}>
              <button
                className={styles.navLink}
                onClick={handleSearch}
                type="button"
              >
                <span>
                  <Search size={19} strokeWidth={1.8} />
                </span>
                Search
              </button>
            </li>

            {/* RBAC Group */}
            {betaEnabled && (
              <li className={styles.navListGroup}>
                <button
                  type="button"
                  className={`${styles.navGroupLabel} ${styles.navGroupLabelButton}`}
                  onClick={() => toggleGroup('rbac')}
                  aria-expanded={expandedGroups.rbac}
                >
                  <span>RBAC</span>
                  {expandedGroups.rbac ? (
                    <ChevronDown
                      size={16}
                      strokeWidth={2}
                      className={styles.navChevron}
                    />
                  ) : (
                    <ChevronRight
                      size={16}
                      strokeWidth={2}
                      className={styles.navChevron}
                    />
                  )}
                </button>
                {expandedGroups.rbac && (
                  <ul className={styles.navSubList}>
                    <li className={styles.navListItem}>
                      <Link
                        to={ROUTES.PERMISSION.ROOT}
                        className={styles.navLink}
                        onClick={closeSidebar}
                      >
                        <span>
                          <Shield size={19} strokeWidth={1.8} />
                        </span>
                        Permissions
                      </Link>
                    </li>
                    <li className={styles.navListItem}>
                      <Link
                        to={ROUTES.COMPANY.USERS}
                        className={styles.navLink}
                        onClick={closeSidebar}
                      >
                        <span>
                          <Users size={19} strokeWidth={1.8} />
                        </span>
                        Users
                      </Link>
                    </li>
                    <li className={styles.navListItem}>
                      <Link
                        to={ROUTES.ROLE.ROOT}
                        className={styles.navLink}
                        onClick={closeSidebar}
                      >
                        <span>
                          <Shield size={19} strokeWidth={1.8} />
                        </span>
                        Roles
                      </Link>
                    </li>
                  </ul>
                )}
              </li>
            )}

            {/* Church Group */}
            {betaEnabled && (
              <li className={styles.navListGroup}>
                <button
                  type="button"
                  className={`${styles.navGroupLabel} ${styles.navGroupLabelButton}`}
                  onClick={() => toggleGroup('church')}
                  aria-expanded={expandedGroups.church}
                >
                  <span>Church</span>
                  {expandedGroups.church ? (
                    <ChevronDown
                      size={16}
                      strokeWidth={2}
                      className={styles.navChevron}
                    />
                  ) : (
                    <ChevronRight
                      size={16}
                      strokeWidth={2}
                      className={styles.navChevron}
                    />
                  )}
                </button>
                {expandedGroups.church && (
                  <ul className={styles.navSubList}>
                    <li className={styles.navListItem}>
                      <Link
                        to={ROUTES.MEMBERSHIP.ROOT}
                        className={styles.navLink}
                        onClick={closeSidebar}
                      >
                        <span>
                          <Users size={19} strokeWidth={1.8} />
                        </span>
                        <span className={styles.navLinkText}>
                          General Member's List
                        </span>
                      </Link>
                    </li>
                    <li className={styles.navListItem}>
                      <Link
                        to={ROUTES.MEMBERSHIP.WEBSITE_BUILDER}
                        className={styles.navLink}
                        onClick={closeSidebar}
                      >
                        <span>
                          <LayoutDashboard size={19} strokeWidth={1.8} />
                        </span>
                        <span className={styles.navLinkText}>
                          Website Builder
                        </span>
                      </Link>
                    </li>
                    <li className={styles.navListItem}>
                      <button
                        type="button"
                        className={styles.navLink}
                        onClick={() => {
                          toggleGroup('churchReports');
                          navigate(ROUTES.CHURCH.REPORTS.ROOT);
                        }}
                        aria-expanded={expandedGroups.churchReports}
                        aria-label="Church Reports"
                      >
                        <span>
                          <FileText size={19} strokeWidth={1.8} />
                        </span>
                        <span className={styles.navLinkText}>
                          Church Reports
                        </span>
                        {expandedGroups.churchReports ? (
                          <ChevronDown
                            size={16}
                            strokeWidth={2}
                            className={styles.navChevron}
                          />
                        ) : (
                          <ChevronRight
                            size={16}
                            strokeWidth={2}
                            className={styles.navChevron}
                          />
                        )}
                      </button>
                      {expandedGroups.churchReports && (
                        <ul className={styles.navMembershipSubList}>
                          <li className={styles.navListItem}>
                            <Link
                              to={ROUTES.CHURCH.REPORTS.MEMBERS}
                              className={styles.navLink}
                              onClick={closeSidebar}
                            >
                              <span>
                                <Users size={19} strokeWidth={1.8} />
                              </span>
                              <span className={styles.navLinkText}>
                                Member&apos;s Lists Report
                              </span>
                            </Link>
                          </li>
                          <li className={styles.navListItem}>
                            <Link
                              to={ROUTES.CHURCH.REPORTS.DEPARTMENTAL_DETAIL.replace(
                                ':dept',
                                'musicAudio',
                              )}
                              className={styles.navLink}
                              onClick={closeSidebar}
                            >
                              <span>
                                <Building2 size={19} strokeWidth={1.8} />
                              </span>
                              <span className={styles.navLinkText}>
                                Departmental Report
                              </span>
                            </Link>
                          </li>
                        </ul>
                      )}
                    </li>
                  </ul>
                )}
              </li>
            )}

            {/* Accounting Group */}
            <li className={styles.navListGroup}>
              <button
                type="button"
                className={`${styles.navGroupLabel} ${styles.navGroupLabelButton}`}
                onClick={() => toggleGroup('accounting')}
                aria-expanded={expandedGroups.accounting}
              >
                <span>Accounting</span>
                {expandedGroups.accounting ? (
                  <ChevronDown
                    size={16}
                    strokeWidth={2}
                    className={styles.navChevron}
                  />
                ) : (
                  <ChevronRight
                    size={16}
                    strokeWidth={2}
                    className={styles.navChevron}
                  />
                )}
              </button>
              {expandedGroups.accounting && (
                <ul className={styles.navSubList}>
                  {betaEnabled && (
                    <li className={styles.navListItem}>
                      <Link
                        to={ROUTES.PRODUCTS.ROOT}
                        className={styles.navLink}
                        onClick={closeSidebar}
                      >
                        <span>
                          <Folder size={19} strokeWidth={1.8} />
                        </span>
                        Products
                      </Link>
                    </li>
                  )}
                  {betaEnabled && (
                    <li className={styles.navListItem}>
                      <Link
                        to={ROUTES.SALES.ROOT}
                        className={styles.navLink}
                        onClick={closeSidebar}
                      >
                        <span>
                          <ArrowRightLeft size={19} strokeWidth={1.8} />
                        </span>
                        Sales
                      </Link>
                    </li>
                  )}
                  {betaEnabled && (
                    <li className={styles.navListItem}>
                      <Link
                        to={ROUTES.EXPENSES.ROOT}
                        className={styles.navLink}
                        onClick={closeSidebar}
                      >
                        <span>
                          <ArrowRightLeft size={19} strokeWidth={1.8} />
                        </span>
                        Disbursements
                      </Link>
                    </li>
                  )}
                  {betaEnabled && (
                    <li className={styles.navListItem}>
                      <Link
                        to={ROUTES.V2.SALES_ORDERS}
                        className={styles.navLink}
                        onClick={closeSidebar}
                      >
                        <span>
                          <ArrowRightLeft size={19} strokeWidth={1.8} />
                        </span>
                        Sales Orders (V2)
                      </Link>
                    </li>
                  )}
                  {betaEnabled && (
                    <li className={styles.navListItem}>
                      <Link
                        to={ROUTES.V2.SALES}
                        className={styles.navLink}
                        onClick={closeSidebar}
                      >
                        <span>
                          <ArrowRightLeft size={19} strokeWidth={1.8} />
                        </span>
                        Sales (V2)
                      </Link>
                    </li>
                  )}
                  {betaEnabled && (
                    <li className={styles.navListItem}>
                      <Link
                        to={ROUTES.V2.PURCHASE_ORDERS}
                        className={styles.navLink}
                        onClick={closeSidebar}
                      >
                        <span>
                          <ArrowRightLeft size={19} strokeWidth={1.8} />
                        </span>
                        Purchase Orders (V2)
                      </Link>
                    </li>
                  )}
                  {betaEnabled && (
                    <li className={styles.navListItem}>
                      <Link
                        to={ROUTES.V2.PURCHASES}
                        className={styles.navLink}
                        onClick={closeSidebar}
                      >
                        <span>
                          <ArrowRightLeft size={19} strokeWidth={1.8} />
                        </span>
                        Purchases (V2)
                      </Link>
                    </li>
                  )}
                  <li className={styles.navListItem}>
                    <Link
                      to={ROUTES.REPORTS.INVENTORY_REPORT}
                      className={styles.navLink}
                      onClick={closeSidebar}
                    >
                      <span>
                        <FileText size={19} strokeWidth={1.8} />
                      </span>
                      Inventory Report
                    </Link>
                  </li>
                </ul>
              )}
            </li>

            {/* Others Group */}
            <li className={styles.navListGroup}>
              <button
                type="button"
                className={`${styles.navGroupLabel} ${styles.navGroupLabelButton}`}
                onClick={() => toggleGroup('others')}
                aria-expanded={expandedGroups.others}
              >
                <span>Others</span>
                {expandedGroups.others ? (
                  <ChevronDown
                    size={16}
                    strokeWidth={2}
                    className={styles.navChevron}
                  />
                ) : (
                  <ChevronRight
                    size={16}
                    strokeWidth={2}
                    className={styles.navChevron}
                  />
                )}
              </button>
              {expandedGroups.others && (
                <ul className={styles.navSubList}>
                  {betaEnabled && (
                    <li className={styles.navListItem}>
                      <Link
                        to={ROUTES.STORAGE.ROOT}
                        className={styles.navLink}
                        onClick={closeSidebar}
                      >
                        <span>
                          <HardDrive size={19} strokeWidth={1.8} />
                        </span>
                        Storage
                      </Link>
                    </li>
                  )}
                  {betaEnabled && (
                    <li className={styles.navListItem}>
                      <Link
                        to={ROUTES.SCHEDULES.ROOT}
                        className={styles.navLink}
                        onClick={closeSidebar}
                      >
                        <span>
                          <Clock size={19} strokeWidth={1.8} />
                        </span>
                        Schedules
                      </Link>
                    </li>
                  )}
                  <li className={styles.navListItem}>
                    <Link
                      to={ROUTES.KANBAN}
                      className={styles.navLink}
                      onClick={closeSidebar}
                    >
                      <span>
                        <LayoutGrid size={19} strokeWidth={1.8} />
                      </span>
                      Kanban
                    </Link>
                  </li>
                  {betaEnabled && (
                    <li className={styles.navListItem}>
                      <Link
                        to={ROUTES.CHAT}
                        className={styles.navLink}
                        onClick={closeSidebar}
                      >
                        <span>
                          <MessageCircle size={19} strokeWidth={1.8} />
                        </span>
                        Chatbot
                      </Link>
                    </li>
                  )}
                  {betaEnabled && (
                    <li className={styles.navListItem}>
                      <Link
                        to={ROUTES.FORMS}
                        className={styles.navLink}
                        onClick={closeSidebar}
                      >
                        <span>
                          <FileText size={19} strokeWidth={1.8} />
                        </span>
                        Downloadable Forms
                      </Link>
                    </li>
                  )}
                </ul>
              )}
            </li>

            {/* Account Group */}
            <li className={styles.navListGroup}>
              <button
                type="button"
                className={`${styles.navGroupLabel} ${styles.navGroupLabelButton}`}
                onClick={() => toggleGroup('account')}
                aria-expanded={expandedGroups.account}
              >
                <span>Account</span>
                {expandedGroups.account ? (
                  <ChevronDown
                    size={16}
                    strokeWidth={2}
                    className={styles.navChevron}
                  />
                ) : (
                  <ChevronRight
                    size={16}
                    strokeWidth={2}
                    className={styles.navChevron}
                  />
                )}
              </button>
              {expandedGroups.account && (
                <ul className={styles.navSubList}>
                  <li className={styles.navListItem}>
                    <button
                      className={styles.navLink}
                      onClick={() => void toggleFullscreen()}
                      type="button"
                    >
                      <span>
                        {isFullscreen ? (
                          <Minimize2 size={19} strokeWidth={1.8} />
                        ) : (
                          <Maximize2 size={19} strokeWidth={1.8} />
                        )}
                      </span>
                      {isFullscreen ? 'Exit Fullscreen' : 'Enter Fullscreen'}
                    </button>
                  </li>
                  <li className={styles.navListItem}>
                    <Link
                      to={ROUTES.USER.SETTINGS}
                      className={styles.navLink}
                      onClick={closeSidebar}
                    >
                      <span>
                        <SlidersHorizontal size={19} strokeWidth={1.8} />
                      </span>
                      App Settings
                    </Link>
                  </li>
                  <li className={styles.navListItem}>
                    <button
                      className={styles.navLink}
                      onClick={handleViewProfile}
                      type="button"
                    >
                      <span>
                        <User size={19} strokeWidth={1.8} />
                      </span>
                      View Profile
                    </button>
                  </li>
                  {betaEnabled && (
                    <li className={styles.navListItem}>
                      <Link
                        to={ROUTES.API_KEYS}
                        className={styles.navLink}
                        onClick={closeSidebar}
                      >
                        <span>
                          <Shield size={19} strokeWidth={1.8} />
                        </span>
                        API Keys
                      </Link>
                    </li>
                  )}
                  <li className={styles.navListItem}>
                    <Link
                      to={ROUTES.COMPANIES.ROOT}
                      className={styles.navLink}
                      onClick={closeSidebar}
                    >
                      <span>
                        <Building2 size={19} strokeWidth={1.8} />
                      </span>
                      Companies
                    </Link>
                  </li>
                  <li className={styles.navListItem}>
                    <button
                      className={styles.navLink}
                      onClick={() => void handleLogout()}
                      type="button"
                    >
                      <span>
                        <LogOut size={19} strokeWidth={1.8} />
                      </span>
                      Logout
                    </button>
                  </li>
                </ul>
              )}
            </li>
          </ul>
        </nav>
        <div className={styles.sidebarHelp}>
          <Link
            to={ROUTES.HELP}
            className={`${styles.helpButton} ${isInHelp(pathname) ? styles.helpButtonActive : ''}`}
            onClick={closeSidebar}
            aria-label="Need Help?"
          >
            <span className={styles.helpIcon}>
              <CircleHelp size={18} strokeWidth={1.9} />
            </span>
            <span className={styles.helpText}>Need Help?</span>
          </Link>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
